import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Download, RefreshCw, XCircle } from "lucide-react";
import { producersApi } from "./api";
import type { ListFilters, ProducerApplication } from "./types";
import { StatusBadge } from "./shared/StatusBadge";
import { FiltersBar } from "./shared/FiltersBar";
import { ApplicationsTable, type Column } from "./shared/ApplicationsTable";
import { ApplicationDrawer } from "./shared/ApplicationDrawer";
import { Section, Field, Link, Chips } from "./shared/InfoBlock";
import {
  countryOptions,
  formatApplicantName,
  formatCountry,
  formatDate,
  formatDateTime,
} from "./shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { useDebounce } from "@/lib/utils/useDebounce";
import { confirmAction } from "@/app/providers/ConfirmDialog";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "contacted", label: "Contactado" },
  { value: "approved", label: "Aprobado" },
  { value: "rejected", label: "Rechazado" },
];

const PAGE_SIZE = 25;

export function ProducersScreen() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, country]);

  const filters: ListFilters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      status: status || undefined,
      country: country || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, status, country, page],
  );

  const listQuery = useQuery({
    queryKey: ["applications", "producers", filters],
    queryFn: () => producersApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ["applications", "producers", "detail", selectedId],
    queryFn: () => producersApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  const exportHref = `${import.meta.env.VITE_API_BASE || "https://api-nexus.mixcore.club"}${producersApi.exportCsv(filters)}`;

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Aplicaciones · Productores</p>
          <h1 className="nx-h1">Postulaciones de productores</h1>
          <p className="text-sm text-fg-muted mt-1">
            Revisá y aprobá productores. Al aprobar se habilita el Hub
            (<code>isProducer=true</code>) y se envía un email automático.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={exportHref} target="_blank" rel="noopener noreferrer" className="nx-btn-secondary">
            <Download size={14} /> Exportar CSV
          </a>
          <button
            onClick={() => listQuery.refetch()}
            className="nx-btn-secondary"
            disabled={listQuery.isFetching}
          >
            <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por organización, ciudad o email…"
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
        country={country}
        onCountryChange={setCountry}
        countryOptions={countryOptions()}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando postulaciones…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<ProducerApplication>
          rows={listQuery.data?.applications ?? []}
          columns={columns}
          page={listQuery.data?.page ?? 1}
          pageSize={listQuery.data?.pageSize ?? PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
        />
      )}

      <ProducerDrawer
        selectedId={selectedId}
        data={detailQuery.data ?? null}
        loading={detailQuery.isLoading && selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

const columns: Column<ProducerApplication>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "organization",
    header: "Organización",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.organizationName}</div>
        <div className="text-2xs text-fg-subtle">{r.organizationType}</div>
      </div>
    ),
  },
  {
    key: "applicant",
    header: "Postulante",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{formatApplicantName(r.applicant)}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.applicant?.email ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "location",
    header: "Ubicación",
    cell: (r) => (
      <div className="text-fg-muted text-xs">
        {r.city}, {formatCountry(r.country)}
      </div>
    ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => <StatusBadge status={r.status} />,
    width: "min",
  },
  {
    key: "createdAt",
    header: "Recibida",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.createdAt)}</span>,
    width: "min",
    align: "right",
  },
];

// Exportado: la Bandeja unificada (InboxScreen) reusa este mismo drawer.
export function ProducerDrawer({
  selectedId,
  data,
  loading,
  onClose,
}: {
  selectedId: number | null;
  data: ProducerApplication | null;
  loading: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["applications", "producers"] });
  };

  const updateNotes = useMutation({
    mutationFn: (notes: string | null) =>
      producersApi.update(selectedId!, { internalNotes: notes }),
    onSuccess: () => {
      push({ kind: "success", message: "Notas guardadas." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudieron guardar las notas." }),
  });

  const markContacted = useMutation({
    mutationFn: () => producersApi.update(selectedId!, { status: "contacted" }),
    onSuccess: () => {
      push({ kind: "success", message: "Marcado como contactado." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo actualizar el estado." }),
  });

  const approve = useMutation({
    mutationFn: () => producersApi.approve(selectedId!),
    onSuccess: () => {
      push({ kind: "success", message: "Postulación aprobada. Email enviado al postulante." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo aprobar." }),
  });

  const reject = useMutation({
    mutationFn: (reason: string | undefined) => producersApi.reject(selectedId!, reason),
    onSuccess: () => {
      push({ kind: "success", message: "Postulación rechazada. Email enviado al postulante." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo rechazar." }),
  });

  return (
    <ApplicationDrawer<ProducerApplication>
      open={selectedId !== null}
      data={data}
      loading={loading}
      title={data ? data.organizationName : "Postulación"}
      subtitle={data ? `#${data.id} · ${formatDateTime(data.createdAt)}` : undefined}
      onClose={onClose}
      onSaveNotes={(notes) => updateNotes.mutateAsync(notes).then(() => undefined)}
      renderBody={(d) => (
        <>
          <div className="flex items-center gap-2">
            <StatusBadge status={d.status} />
          </div>

          <Section title="Organización">
            <Field label="Nombre" value={d.organizationName} wide />
            <Field label="Tipo" value={d.organizationType} />
            <Field label="Experiencia" value={d.experience} />
            <Field label="País" value={formatCountry(d.country)} />
            <Field label="Ciudad" value={d.city} />
          </Section>

          <Section title="Volumen">
            <Field label="Tamaño de evento" value={d.eventSize} />
            <Field label="Frecuencia" value={d.eventFrequency} />
            <Field label="Vendiendo actualmente" value={d.currentlySelling} />
            <Field label="Géneros" value={<Chips items={d.genres} />} wide />
          </Section>

          <Section title="Comercial & contacto">
            <Field label="Razón social" value={d.hasBusinessId ? "Sí" : "No"} />
            <Field label="ID fiscal" value={d.businessIdValue ?? "—"} />
            <Field
              label="Sitio web"
              value={d.website ? <Link href={d.website}>{d.website}</Link> : "—"}
              wide
            />
            <Field
              label="WhatsApp"
              value={
                // Las altas por invitación pueden venir sin WhatsApp.
                d.whatsapp ? (
                  <a
                    className="text-accent hover:underline"
                    href={`https://wa.me/${d.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {d.whatsapp}
                  </a>
                ) : null
              }
            />
            <Field
              label="Email"
              value={
                d.applicant?.email ? (
                  <a className="text-accent hover:underline" href={`mailto:${d.applicant.email}`}>
                    {d.applicant.email}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            <Field label="Postulante" value={formatApplicantName(d.applicant)} wide />
            <Field label="Comentarios adicionales" value={d.additionalComments ?? "—"} wide />
          </Section>

          {(d.contactedAt || d.approvedAt || d.rejectedAt) && (
            <Section title="Historia">
              {d.contactedAt && <Field label="Contactado" value={formatDateTime(d.contactedAt)} wide />}
              {d.approvedAt && <Field label="Aprobado" value={formatDateTime(d.approvedAt)} wide />}
              {d.rejectedAt && (
                <>
                  <Field label="Rechazado" value={formatDateTime(d.rejectedAt)} wide />
                  {d.rejectionReason && <Field label="Razón" value={d.rejectionReason} wide />}
                </>
              )}
            </Section>
          )}
        </>
      )}
      renderActions={(d) => {
        const isFinal = d.status === "approved" || d.status === "rejected";
        return (
          <>
            {d.status === "pending" && (
              <button
                onClick={() => markContacted.mutate()}
                disabled={markContacted.isPending}
                className="nx-btn-secondary"
              >
                Marcar contactado
              </button>
            )}
            <button
              onClick={async () => {
                const reason = await confirmAction({
                  title: "Rechazar postulación",
                  message: "El postulante recibirá un email con la razón (opcional).",
                  promptLabel: "Razón del rechazo (opcional)",
                  confirmLabel: "Rechazar",
                  tone: "danger",
                });
                if (reason !== null) reject.mutate((reason as string) || undefined);
              }}
              disabled={reject.isPending || isFinal}
              className="nx-btn-danger"
            >
              <XCircle size={14} /> Rechazar
            </button>
            <button
              onClick={async () => {
                const ok = await confirmAction({
                  title: "Aprobar postulación",
                  message: `Se habilitará el Hub para ${formatApplicantName(d.applicant)} y se enviará el email de bienvenida.`,
                  confirmLabel: "Aprobar",
                  tone: "success",
                });
                if (ok === true) approve.mutate();
              }}
              disabled={approve.isPending || isFinal}
              className="nx-btn-primary"
            >
              <CheckCircle2 size={14} /> Aprobar
            </button>
          </>
        );
      }}
    />
  );
}
